/**
 * Shared constants and sizing math for the QR label print flow (SC-081,
 * five standard sizes added SC-086).
 *
 * `LABEL_PRESETS`' ids must match the backend's `LabelPreset` Literal at
 * apps/auth-product-ai/src/auth_product_ai/schemas_organizations.py:50
 * exactly: the backend 422s a PUT whose `preset` isn't one of these ten
 * ids or null. An id drifting out of sync here would make every Print
 * silently fail to persist (caught, non-blocking, but still wrong).
 *
 * Ordered by ascending label area so the smallest, most common sizes sort
 * first in the selector; all presets sort before the Custom option, which
 * QRCodeModal renders separately after this list.
 */

export interface LabelPreset {
  key: string;
  label: string;
  width: number;
  height: number;
}

export const LABEL_PRESETS = [
  { key: '1x1', label: '1" x 1"', width: 1, height: 1 },
  { key: '2x1', label: '2" x 1"', width: 2, height: 1 },
  { key: '1x2.125', label: '1" x 2.125" (DYMO)', width: 1, height: 2.125 },
  { key: '1.5x1.5', label: '1.5" x 1.5"', width: 1.5, height: 1.5 },
  { key: '2.25x1.25', label: '2.25" x 1.25"', width: 2.25, height: 1.25 },
  { key: '3.5x1.125', label: '3.5" x 1.125" (Address)', width: 3.5, height: 1.125 },
  { key: '2x2', label: '2" x 2"', width: 2, height: 2 },
  { key: '2x3', label: '2" x 3"', width: 2, height: 3 },
  { key: '4x3', label: '4" x 3"', width: 4, height: 3 },
  { key: '4x6', label: '4" x 6" (Shipping)', width: 4, height: 6 },
] as const satisfies readonly LabelPreset[];

export type LabelPresetKey = (typeof LABEL_PRESETS)[number]['key'];

export const CUSTOM_KEY = 'custom';
export const DEFAULT_PRESET_KEY: LabelPresetKey = '2.25x1.25';
export const MIN_LABEL_IN = 0.5;
export const MAX_LABEL_IN = 12.0;
export const MIN_QR_SIZE_IN = 0.3;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function findPreset(key: string): LabelPreset {
  // Fallback contract: an unknown key resolves to the declared default
  // preset (DEFAULT_PRESET_KEY), never a positional index, so reordering
  // LABEL_PRESETS can't silently change what "unknown" falls back to.
  return (
    LABEL_PRESETS.find((p) => p.key === key) ??
    LABEL_PRESETS.find((p) => p.key === DEFAULT_PRESET_KEY)!
  );
}

export function isKnownPresetKey(key: unknown): key is LabelPresetKey {
  return typeof key === 'string' && LABEL_PRESETS.some((p) => p.key === key);
}

/**
 * Parses and bounds an untrusted dimension (a GET response is server data,
 * not a compile-time-typed value: it can arrive as a string, an
 * out-of-range number, or garbage). Falls back rather than propagating NaN
 * or an out-of-bounds size into the rendered inputs or the print output.
 */
export function sanitizeDimension(value: unknown, fallback: number): number {
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(num)) return fallback;
  return clamp(num, MIN_LABEL_IN, MAX_LABEL_IN);
}

/**
 * Splits a label into a title band and a QR square that both fit within
 * width x height inches, leaving a quiet margin so handheld scanners can
 * still lock onto the QR's finder patterns near the label edge.
 */
export function computeLabelLayout(widthIn: number, heightIn: number): { marginIn: number; qrSizeIn: number } {
  const marginIn = clamp(Math.min(widthIn, heightIn) * 0.08, 0.04, 0.15);
  const titleBandIn = clamp(heightIn * 0.22, 0.12, 0.4);
  const availableWidthIn = Math.max(MIN_QR_SIZE_IN, widthIn - marginIn * 2);
  const availableHeightIn = Math.max(MIN_QR_SIZE_IN, heightIn - titleBandIn - marginIn * 2);
  const qrSizeIn = Math.max(MIN_QR_SIZE_IN, Math.min(availableWidthIn, availableHeightIn));
  return { marginIn, qrSizeIn };
}

/** Escapes text before it is interpolated into an HTML string written via document.write. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
